<template>
  <div>
    <TutorsConditionsComponent class="mb-2"
      v-if="!isConfirm"
      :initialConditions="conditions"
      @changeConditions="changeConditions" />

    <div v-else class="row container">
      <div class="col-4 form-group" v-for="condition of conditionsOnConfirm" :key="condition.key">
        <label>{{ condition.title }}</label>
        <div>{{ condition.value }}</div>
      </div>
    </div>

    <div>
      <PagerComponent v-model="pager" @changePage="changePage" class="mb-2" />
      <b-table striped hover fixed Outlined show-empty
        :items="tutors"
        :fields="fields"
        :empty-text="'No data available'"
        :table-class="'list-table text-center'"
        :thead-class="'list-table__head'"
        :tbody-class="'list-table__body'"
      />
    </div>

    <div v-if="!isConfirm" class="d-flex justify-content-end">
      <b-button pill variant="secondary" class="mr-2" @click="back">
        Back
      </b-button>
      <b-button pill variant="primary" @click="next">
        Next
      </b-button>
    </div>

    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import _ from 'lodash'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { Tutor, TutorConditions } from '@/store/tutors/types'
import { RootState } from '@/store/types'
import { Pager } from '@/store/pager.types'
import {
  Nationality,
  Position,
} from '@/store/master/types'
import { genderCode, Genders } from '@/store/common.types'
import TutorsConditionsComponent from '@/components/parts/mails/sendBulk/TutorsConditions.vue'
import PagerComponent from '@/components/parts/Pager.vue'
import Loading from '@/components/parts/modal/Loading.vue'
import { Moment } from 'moment';

interface ConfditionMapping {
  title: string
  formatter?: Function
}

@Component({
  components: {
    TutorsConditionsComponent,
    PagerComponent,
    Loading,
  },
})
export default class MailsSendBulkSelectTutors extends Vue {
  @Prop({ required: true }) value!: TutorConditions|undefined

  @Prop({ default: false }) isConfirm!: boolean

  private isLoading: boolean = false

  private fields = [
    {
      key: 'id',
      label: 'Tutor ID',
    },
    {
      key: 'name',
      label: 'Tutor Name',
    },
    {
      key: 'position',
      label: 'Position',
      formatter: (position: Position) => position.name,
    },
    {
      key: 'hireDate',
      label: 'Start Date',
      formatter: (hireDate: Moment|undefined) => hireDate && hireDate.format('YYYY/MM/DD'),
    },
    {
      key: 'phone1',
      label: 'Phone',
    },
    {
      key: 'email1',
      label: 'Email',
    },
    {
      key: 'address',
      label: 'Address',
    },
  ]

  private get tutors(): Tutor[] {
    return (this.$store.state as RootState).tutors.tutors
  }

  private get pager(): Pager {
    return (this.$store.state as RootState).tutors.pager
  }

  private get conditions(): TutorConditions|undefined {
    return this.value
  }

  private set conditions(conditions: TutorConditions|undefined) {
    this.$emit('input', conditions)
  }

  private get conditionsOnConfirm() {
    const conditionsMapping: { [name: string]: {
      title: string,
      formatter?: Function,
    }} = {
      tutorNameOrId: {
        title: 'Tutor',
      },
      positions: {
        title: 'Position',
        formatter: (ps: Position[]): string => ps.map((p) => p.name).join(', '),
      },
      genderCodes: {
        title: 'Gender',
        formatter: (gs: genderCode[]): string => gs.map((g) => Genders[g]).join(', '),
      },
      isIncludeTerminationOfService: {
        title: 'Include Past Tutors',
        formatter: (b: boolean) => (b ? 'Include' : 'Not include'),
      },
      nationality: {
        title: 'Nationality',
        formatter: (n: Nationality) => n.name,
      },
    }

    return _.chain(this.conditions)
      .pickBy((v) => v !== undefined && v !== null && !_.isEmpty(v))
      .map((value, key) => {
        const mapping = conditionsMapping[key]
        return {
          key,
          title: mapping.title,
          value: mapping.formatter ? mapping.formatter(value) : value,
        }
      })
      .value()
  }

  private created() {
    this.search(this.conditions)
  }

  /**
   * チューターを検索
   */
  private search(conditions: TutorConditions|undefined, page: number = 1): void {
    if (this.isLoading) {
      return
    }
    this.isLoading = true
    this.$store.dispatch('fetchTutors', { conditions, page })
      .then(() => {
        this.conditions = conditions
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  /**
   * チューター検索条件変更
   */
  private changeConditions(conditions: TutorConditions): void {
    this.search(conditions)
  }

  /**
   * ページ変更
   */
  private changePage(page: number): void {
    this.search(this.conditions, page)
  }

  private back() {
    this.$emit('back')
  }

  private next() {
    const limit = 5000
    const count = (this.$store.state as RootState).tutors.pager.totalLength
    if (count > limit) {
      this.$bvModal.msgBoxOk(`Please resetting conditions under ${limit} tutors`)
    } else if (count === 0) {
      this.$bvModal.msgBoxOk('Selected 0 tutors, Please resetting conditions 1 or more tutors')
    } else {
      this.$emit('next')
    }
  }
}
</script>

<style scoped>
.list-table {
  word-break : break-all;
}
</style>
